import { usePost } from 'lib/api';
import { useNotification } from 'features/notifications';
import { t } from '@lingui/macro';
import type { TableState, ExportType } from 'features/misc/table/types';
import type { Url } from 'lib/api/hooks';
import { getQuery } from 'utils/table';
import type { FilterParams } from 'types/table';
import { capitalizeFirstLetter } from 'utils';

export interface TableExportParams {
  exportType: ExportType;
  selectedColumns?: string[];
  filterCriteria: TableState;
}

export interface UseTableExportOptions {
  apiEndpoint: Url;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  filters?: FilterParams;
}

export const useTableExport = ({
  apiEndpoint,
  onSuccess,
  onError,
  filters,
}: UseTableExportOptions) => {
  const { notify } = useNotification();

  const { mutate, isPending } = usePost<{
    id: string;
    exportType: ExportType;
    selectedColumns: string[];
  }>(apiEndpoint, false);

  const exportTable = ({ exportType, selectedColumns, filterCriteria }: TableExportParams) => {
    // Apply the same transformation as GET API for consistency
    const transformedFilterCriteria = {
      ...filterCriteria,
      sort: filterCriteria?.sort ? capitalizeFirstLetter(filterCriteria.sort) : undefined,
      order: filterCriteria?.order ? filterCriteria.order.toUpperCase() : undefined,
    };

    // Use the same getQuery pattern as GET API for consistency
    const queryParams = filters
      ? getQuery(transformedFilterCriteria, filters)
      : new URLSearchParams();

    // // Debug logging
    // console.log('Export filterCriteria:', filterCriteria);
    // console.log('Export transformedFilterCriteria:', transformedFilterCriteria);
    // console.log('Export queryParams:', queryParams.toString());
    // console.log('Export queryParams entries:', Array.from(queryParams.entries()));
    // console.log('Export filters:', filters);

    mutate(
      {
        data: {
          id: 'export-request',
          exportType,
          selectedColumns,
        },
        query: queryParams,
      },
      {
        onSuccess: () => {
          notify.success({
            header: t`Your export is being created.`,
            message: t`This may take a few minutes before it is sent to your email.`,
            id: 'table-export-success',
            actions: [{ id: 'close', label: t`Close` }],
          });
          onSuccess?.();
        },
        onError: error => {
          notify.error({
            header: t`Export failed`,
            message: t`There was an error creating your export. Please try again.`,
            id: 'table-export-error',
            actions: [{ id: 'close', label: t`Close` }],
          });
          onError?.(error);
        },
      }
    );
  };

  return {
    exportTable,
    isPending,
  };
};
